import { Component, Input, OnChanges, OnInit, Output, EventEmitter, SimpleChanges } from '@angular/core';

import { StateService } from '@uirouter/core';
import { tap } from 'rxjs/operators';

import { FileUtil } from 'rev-shared/util/FileUtil';
import { FileWrapper } from 'rev-shared/ui/fileUpload/FileWrapper';
import { SecurityRedirectHelperService } from 'rev-shared/security/SecurityRedirectHelper.Service';
import { SessionService } from 'rev-shared/security/Session.Service';
import { UserAccountService } from 'rev-shared/security/UserAccount.Service';
import { VideoService } from 'rev-shared/media/Video.Service';
import { VideoSourceType } from 'rev-shared/media/VideoSourceType';
import { VideoStatus } from 'rev-shared/media/VideoStatus';
import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';
import { noop } from 'rev-shared/util';

import styles from './replace-video-dialog.module.less';

interface IFileInputErrors {
	fileSize: boolean;
	fileType: boolean;
}

interface IUploadErrors {
	processingFailed: boolean;
	uploadFailed: boolean;
}

enum ReplaceVideoStage {
	SELECT_FILE = 1,
	CONFIRMATION = 2,
	UPLOAD_PROGRESS = 3,
	PROCESSING = 4,
	COMPLETE = 5
}

@Component({
	selector: 'replace-video-dialog',
	templateUrl: './ReplaceVideoDialog.Component.html'
})
export class ReplaceVideoDialogComponent implements OnChanges, OnInit {
	@Input() public isOpen: boolean;
	@Input() public video: any;
	@Output() public onClose: EventEmitter<any> = new EventEmitter();

	public readonly ReplaceVideoStage = ReplaceVideoStage;
	public readonly styles = styles;

	public account: any;
	public fileInputErrors: IFileInputErrors;
	public isRevert: boolean = false;
	public noop = noop;
	public replaceVideoStage: ReplaceVideoStage;
	public uploadErrors: IUploadErrors;
	public uploadProgress: number;

	private uploadFile: FileWrapper;

	constructor(
		private $state: StateService,
		private SecurityRedirectHelper: SecurityRedirectHelperService,
		private Session: SessionService,
		private UserAccount: UserAccountService,
		private VideoService: VideoService
	) { }

	public ngOnInit(): void {
		this.UserAccount.fetchAccountDetail(this.UserAccount.workingAccountId)
			.then(account => this.account = account)
			.catch(err => this.SecurityRedirectHelper.onError(err, this.$state.current.name));

		switch (this.video.status) {
			case VideoStatus.UPLOADING:
				this.replaceVideoStage = ReplaceVideoStage.UPLOAD_PROGRESS;
				break;

			case VideoStatus.PROCESSING:
				this.replaceVideoStage = ReplaceVideoStage.PROCESSING;
				break;

			default:
				this.resetUploadErrors();
				this.replaceVideoStage = ReplaceVideoStage.SELECT_FILE;
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.isOpen && this.isOpen) {
			this.onIsOpenChange();
		}

		if (changes.video && this.video) {
			this.onVideoChange();
		}
	}

	public get canRevert(): boolean {
		return this.replaceVideoStage === ReplaceVideoStage.UPLOAD_PROGRESS || this.isUploadError;
	}

	public get isUploadError(): boolean {
		const uploadErrors: IUploadErrors = this.uploadErrors;

		if (!uploadErrors) {
			return false;
		}

		return uploadErrors.processingFailed ||
			uploadErrors.uploadFailed;
	}

	private close(): void {
		this.onClose.emit();
	}

	public onCancel(): void {
		this.close();
	}

	public onConfirm(): void {
		this.replaceVideoStage = ReplaceVideoStage.UPLOAD_PROGRESS;
		this.replaceVideo();
	}

	public onFileAdd(file: FileWrapper): void {
		if (!this.validateFile(file)) {
			return;
		}

		this.uploadFile = file;
		this.resetProgress();
		this.replaceVideoStage = ReplaceVideoStage.CONFIRMATION;
	}

	public onIsOpenChange(): void {
		if (this.replaceVideoStage === ReplaceVideoStage.COMPLETE) {
			this.reset();
		}
	}

	public onOk(): void {
		this.close();
	}

	public onRetry(): void {
		this.resetUploadErrors();
		this.replaceVideoStage = ReplaceVideoStage.SELECT_FILE;
	}

	public onRevert(): void {
		if (this.uploadFile) {
			this.uploadFile.abort();
		}

		this.VideoService.revertVideo(this.video.id);

		this.replaceVideoStage = ReplaceVideoStage.PROCESSING;
		this.isRevert = true;
	}

	private onVideoChange(): void {
		let status: VideoStatus = this.video.status;

		if (this.video.status === VideoStatus.READY && this.video.isProcessing) { // faster playback funcitonality may set status to READY while still processing
			status = VideoStatus.PROCESSING;
		}

		switch (status) {
			case VideoStatus.PROCESSING:
				this.replaceVideoStage = ReplaceVideoStage.PROCESSING;
				break;

			case VideoStatus.UPLOAD_FAILED:
			case VideoStatus.PROCESSING_FAILED:
				this.uploadErrors = {
					processingFailed: status === VideoStatus.PROCESSING_FAILED,
					uploadFailed: status === VideoStatus.UPLOAD_FAILED
				};

				this.replaceVideoStage = ReplaceVideoStage.CONFIRMATION;
				break;

			case VideoStatus.READY:
			case VideoStatus.READY_BUT_PROCESSING_FAILED:
				this.resetUploadErrors();
				this.setReplaceVideoStageComplete();
				break;

			case VideoStatus.VIEWING_HOURS_NOT_AVAILABLE:
				this.setReplaceVideoStageComplete();
				break;
		}
	}

	private replaceVideo(): void {
		const sessionKeepalive = this.Session.createKeepalive();
		sessionKeepalive.begin();

		this.resetProgress();

		this.VideoService.replaceVideo(this.video.id, this.uploadFile.name, this.uploadFile.size)
			.then(url => this.uploadVideo(this.uploadFile, url))
			.finally(() => {
				this.uploadFile = null;
				sessionKeepalive.end();
			});
	}

	private reset(): void {
		this.replaceVideoStage = ReplaceVideoStage.SELECT_FILE;
		this.resetProgress();
		this.resetUploadErrors();
	}

	private resetProgress(): void {
		this.uploadProgress = 0;
	}

	private resetUploadErrors(): void {
		this.uploadErrors = null;
		this.fileInputErrors = null;
	}

	private setReplaceVideoStageComplete(): void {
		if (this.replaceVideoStage === ReplaceVideoStage.PROCESSING) {
			this.replaceVideoStage = ReplaceVideoStage.COMPLETE;
		}
	}

	private uploadVideo(file: FileWrapper, url: string): Promise<void> {
		file.setOptions({ url });

		return lastValueFrom(file.submit$.pipe(
			tap(e => {
				if (e.isProgress) {
					this.uploadProgress = e.loaded / e.total * 100;
				}
			})));
	}

	private validateFile(file: FileWrapper): boolean {
		this.fileInputErrors = {
			fileSize: this.validateFileSize(file),
			fileType: this.validateFileType(file)
		};

		return !this.fileInputErrors.fileType && !this.fileInputErrors.fileSize;
	}

	private validateFileSize(file: FileWrapper): boolean {
		return this.account.maxFileUploadSizeGb > 0 &&
			file.size > (this.account.maxFileUploadSizeGb * FileUtil.Gb);
	}

	private validateFileType(file: FileWrapper): boolean {
		return this.video.sourceType !== VideoSourceType.WEBEX_LIVE_STREAM &&
			this.video.sourceType !== VideoSourceType.RTMP_STREAM &&
			this.video.sourceType !== VideoSourceType.LIVE_ENRICHMENT &&
			this.video.sourceType !== VideoSourceType.WEBEX_EVENTS &&
			this.video.videoConference && this.video.hasDualStreams
			? !file.isZipFile
			: !file.isVideoFile;
	}
}
